import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import LocalStorage from "./api/localStorage";
import { getApiConfig } from "./api/apiConfig";
import { errorLogger, ErrorCategory, ErrorSeverity } from "./services/errorLogger.service";
import { retryTokenRefresh } from "./config/retryConfig";

const isBrowser = typeof window !== "undefined";

const headers: Readonly<Record<string, string>> = {
  Accept: "application/json",
  "Content-Type": "application/json; charset=UTF-8",
};

// Flag to prevent multiple simultaneous token refreshes
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const injectToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const jwt = LocalStorage.readString("authToken");

  if (jwt !== null) {
    config.headers.Authorization = `Bearer ${jwt}`;
  }

  // Customer emulation support
  const emulatedCustomerId = LocalStorage.readString("emulatedCustomerId");
  if (emulatedCustomerId) {
    config.headers["X-Emulate-Customer-Id"] = emulatedCustomerId;
  }

  return config;
};

// Create axios instance
const apiConfig = getApiConfig();
const api = axios.create({
  baseURL: apiConfig.url,
  timeout: 120000, // 2 minutes
  headers,
});

// Request interceptor
api.interceptors.request.use(injectToken, (error) => Promise.reject(error));

// Response interceptor with enhanced error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    // Log errors with context
    if (error.response) {
      const severity = status && status >= 500 ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM;
      const category = status === 401 || status === 403
        ? ErrorCategory.AUTHENTICATION
        : ErrorCategory.NETWORK;

      errorLogger.logError(
        `API Error: ${status} - ${error.message}`,
        error,
        severity,
        category,
        {
          url: originalRequest?.url,
          method: originalRequest?.method,
          additionalData: { statusCode: status },
        }
      );
    } else if (error.request) {
      errorLogger.logNetworkError(
        error.message || "Network request failed",
        0,
        {
          url: originalRequest?.url,
          method: originalRequest?.method,
        }
      );
    }

    // Handle 401 Unauthorized - Token expired
    if (status === 401 && originalRequest && !originalRequest._retry) {
      // Skip refresh for login/refresh endpoints
      if (originalRequest.url?.includes("/Auth/Login") ||
          originalRequest.url?.includes("/Auth/refresh")) {
        forceLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshSuccess = await retryTokenRefresh(
          async () => await performTokenRefresh(),
          {
            onRetry: (_err, attempt, delay) => {
              console.log(`Retrying token refresh (${attempt}) after ${delay}ms`);
            },
          }
        );

        if (refreshSuccess) {
          const newToken = LocalStorage.readString("authToken");
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            onTokenRefreshed(newToken);
          }
          isRefreshing = false;
          return api(originalRequest);
        } else {
          errorLogger.logAuthError("Token refresh failed after retries");
          isRefreshing = false;
          forceLogout();
          return Promise.reject(error);
        }
      } catch {
        errorLogger.logAuthError("Token refresh error", {
          additionalData: { originalError: error.message },
        });
        isRefreshing = false;
        forceLogout();
        return Promise.reject(error);
      }
    }

    // Handle 423 Locked - Session is locked on server
    if (status === 423) {
      errorLogger.logAuthError("Session locked by server", {
        url: originalRequest?.url,
      });
      handleSessionLocked();
      return Promise.reject(error);
    }

    // Handle 403 Forbidden
    if (status === 403) {
      errorLogger.logAuthorizationError("Access denied", {
        url: originalRequest?.url,
      });
    }

    return Promise.reject(error);
  }
);

/**
 * Perform token refresh
 */
async function performTokenRefresh(): Promise<boolean> {
  const refreshToken = LocalStorage.readString("refreshToken");

  if (!refreshToken) {
    console.warn("No refresh token available");
    errorLogger.logAuthError("No refresh token available");
    return false;
  }

  try {
    const response = await axios.post(
      "/api/Auth/refresh",
      { refreshToken },
      {
        headers: { "Content-Type": "application/json" },
        baseURL: getApiConfig().url,
      }
    );

    if (response.data && response.data.accessToken) {
      LocalStorage.saveString("authToken", response.data.accessToken);

      if (response.data.refreshToken) {
        LocalStorage.saveString("refreshToken", response.data.refreshToken);
      }

      // Dispatch custom event for app-wide handling
      if (isBrowser) {
        window.dispatchEvent(
          new CustomEvent("sessionRefreshed", {
            detail: { accessToken: response.data.accessToken },
          })
        );
      }

      console.log("Token refreshed successfully");
      return true;
    }

    errorLogger.logAuthError("Token refresh response invalid");
    return false;
  } catch (error) {
    console.error("Token refresh failed:", error);
    errorLogger.logAuthError("Token refresh request failed", {
      additionalData: { error: error instanceof Error ? error.message : String(error) },
    });
    return false;
  }
}

/**
 * Handle session locked by server
 */
function handleSessionLocked(): void {
  console.log("Session locked by server");

  if (isBrowser) {
    window.dispatchEvent(new CustomEvent("sessionLockedByServer"));
    LocalStorage.saveString("sessionLocked", "true");
  }
}

/**
 * Force logout and redirect to login
 */
function forceLogout(): void {
  console.log("Forcing logout due to authentication failure");

  LocalStorage.delete("authToken");
  LocalStorage.delete("refreshToken");
  LocalStorage.delete("emulatedCustomerId");
  LocalStorage.delete("emulatedCustomerName");
  LocalStorage.delete("sessionLocked");

  if (isBrowser) {
    window.dispatchEvent(new CustomEvent("sessionExpired"));

    // Redirect to login only if not already there
    if (!window.location.pathname.includes("/portal/login")) {
      setTimeout(() => {
        window.location.href = "/portal/login";
      }, 100);
    }
  }
}

/**
 * Login function
 */
export const login = async (email: string, password: string) => {
  try {
    const response = await api.post("/api/Auth/Login", {
      email,
      password,
    });

    const { accessToken, refreshToken } = response.data.data;

    LocalStorage.saveString("authToken", accessToken);
    if (refreshToken) {
      LocalStorage.saveString("refreshToken", refreshToken);
    }

    console.log("[Login] Success! Token saved");

    return response.data;
  } catch (error) {
    console.error("[Login] Failed:", error);
    throw error;
  }
};

/**
 * Logout function
 */
export const logout = () => {
  LocalStorage.delete("authToken");
  LocalStorage.delete("refreshToken");
  LocalStorage.delete("emulatedCustomerId");
  LocalStorage.delete("emulatedCustomerName");
  LocalStorage.delete("sessionLocked");

  console.log("[Logout] User disconnected");

  if (isBrowser) {
    window.dispatchEvent(new CustomEvent("userLoggedOut"));
    window.location.href = "/portal/login";
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return LocalStorage.exists("authToken");
};

/**
 * Check if session is locked
 */
export const isSessionLocked = (): boolean => {
  return LocalStorage.readString("sessionLocked") === "true";
};

/**
 * Get current auth token
 */
export const getAuthToken = (): string | null => {
  return LocalStorage.readString("authToken");
};

export default api;
